import { useState } from "react";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";
import { api, errMsg, formatDate } from "@/lib/api";
import { FoundingBadge } from "@/components/MarketBits";

export default function PlanCard({ seller, freeUntil, onChange }) {
  const [busy, setBusy] = useState(false);
  const status = seller?.plan_status || "none";
  const active = seller?.plan_active;
  const onTrial = seller?.trial_end && new Date(seller.trial_end) > new Date();

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/seller/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  const change = async (action) => {
    if (action === "cancel" && !window.confirm(onTrial
      ? "Cancel your plan? Your adverts stay up until 1 January 2027, and you'll never be charged."
      : "Cancel your plan? It stays active until the end of the month you've paid for, then stops. No refund for the current month.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/seller/subscription/${action}`);
      onChange(data);
      toast.success(action === "cancel"
        ? (onTrial ? "Cancelled. You won't be charged, and your adverts stay up until 1 January 2027" : "Cancelled: your plan ends at the end of this paid month")
        : "Your plan will keep renewing");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#e2dcd4] bg-white p-6 sm:p-8" data-testid="seller-plan-card" data-status={status}>
      <p className="eyebrow flex items-center gap-2"><CreditCard size={13} /> Seller plan</p>
      {seller?.founding_seller && <FoundingBadge className="mt-3" />}
      {active ? (
        <>
          <div className="mt-4 flex items-end gap-3">
            <span className="font-display text-5xl text-[#1f402b]" data-testid="seller-credits">{seller.credits}</span>
            <span className="pb-2 text-sm text-[#5a665e]">of 28 credits left this month</span>
          </div>
          <ul className="mt-5 space-y-1.5 text-sm text-[#3c4a41]">
            <li data-testid="seller-plan-status">Status: <strong>{status === "canceling" ? (onTrial ? "Cancelled, you won't be charged" : "Cancelled, ends at period end") : onTrial ? "Free listing period" : "Active"}</strong></li>
            {onTrial && status !== "canceling" && <li data-testid="seller-trial-note">Free until {formatDate(seller.trial_end)}, then NZ$5 a month. Cancel before then and you won't be charged.</li>}
            {onTrial && status === "canceling" && <li data-testid="seller-trial-note">Your adverts stay up until {formatDate(seller.trial_end)}. You won't be charged.</li>}
            {seller.current_period_end && !seller.is_demo && !onTrial && <li>{status === "canceling" ? "Plan ends" : "Renews"}: {formatDate(seller.current_period_end)}</li>}
            {seller.credits_period_end && !seller.is_demo && <li>Credits reset: {formatDate(seller.credits_period_end)}</li>}
            {seller.price_locked_until && <li data-testid="seller-price-lock">Your $5 rate is locked until {formatDate(seller.price_locked_until)}</li>}
          </ul>
          {!seller.is_demo && (status === "canceling"
            ? <button disabled={busy} onClick={() => change("resume")} className="mt-6 border border-[#1f402b] text-[#1f402b] px-5 py-2.5 text-sm" data-testid="seller-resume-button">Keep my plan</button>
            : <button disabled={busy} onClick={() => change("cancel")} className="mt-6 text-sm text-[#5a665e] underline" data-testid="seller-cancel-button">Cancel plan</button>)}
        </>
      ) : (
        <>
          {freeUntil && (
            <p className="mt-4 inline-block bg-[#eef3ee] text-[#1f402b] px-3 py-1.5 text-sm" data-testid="seller-free-banner">
              <strong>FREE</strong> to list until 1 January 2027
            </p>
          )}
          <p className="mt-4 font-display text-4xl text-[#1f402b]">{freeUntil ? "Free" : "NZ$5"} <span className="text-base text-[#5a665e]">{freeUntil ? "until 1 Jan 2027, then NZ$5 / month" : "/ month"}</span></p>
          <ul className="mt-4 space-y-1.5 text-sm text-[#3c4a41]">
            <li>28 credits every month. 1 credit = 1 advert for 30 days</li>
            {freeUntil && <li>We check your card is real so every seller is genuine. Nothing is charged until 1 January 2027</li>}
            <li>Your $5 rate is locked in for 12 months{freeUntil ? " from when paying starts" : ""}</li>
            <li>Cancel any time. {freeUntil ? "Cancel before 1 January 2027 and you're never charged" : "It takes effect at the end of your paid month"}</li>
            {freeUntil && <li>Join before 1 January 2027 and get a permanent <strong>Founding seller</strong> badge on your adverts</li>}
            <li>Paid monthly in advance. No refunds for part months</li>
          </ul>
          {status === "past_due" && <p className="mt-4 text-sm text-[#991b1b]" data-testid="seller-past-due">Your last payment didn't go through. Subscribe again to keep selling.</p>}
          <button disabled={busy || !seller} onClick={subscribe} className="mt-6 bg-[#c5903b] text-white px-6 py-3 text-sm hover:bg-[#a97a2f] disabled:opacity-50" data-testid="seller-subscribe-button">
            {busy ? "Opening checkout…" : freeUntil ? "Start listing free" : "Subscribe for $5 a month"}
          </button>
          {!seller && <p className="mt-2 text-xs text-[#5a665e]">Save your seller profile first.</p>}
          <p className="mt-3 font-mono-k text-[10px] text-[#c5903b]" data-testid="seller-test-mode-note">TEST MODE: use card 4242 4242 4242 4242. No real money is taken.</p>
        </>
      )}
    </div>
  );
}
